<script setup lang="ts">
import {computed} from 'vue'
import DropdownComponent from './DropdownComponent.vue'
import BottomSheetComponent from './BottomSheetComponent.vue'
import {Breakpoint, useResponsive} from '@toolify/client/src/composables/useResponsive'

import {BottomSheetComponentProps} from '../types/BottomSheetComponentProps'
import {DropdownComponentProps} from '../types/DropdownComponentProps'
import {ResponsiveDropdownComponentForceModeType} from '../enum/ResponsiveDropdownComponentForceModeType'

interface Props  {
  dropdownProps?: DropdownComponentProps
  bottomSheetProps?: BottomSheetComponentProps
  forceMode?: ResponsiveDropdownComponentForceModeType
}

interface Emits {
  (e: 'hide'): void
  (e: 'save'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const {isSmallerThan} = useResponsive()

const mode = computed(() => {
  if (props.forceMode) return props.forceMode
  return isSmallerThan(Breakpoint.Large).value ? ResponsiveDropdownComponentForceModeType.BottomSheet : ResponsiveDropdownComponentForceModeType.Dropdown
})  

const onClose = () => emit('hide')
const onSave = () => emit('save')
</script>

<template>
  <DropdownComponent
    v-if="mode === ResponsiveDropdownComponentForceModeType.Dropdown"
    v-bind="{ ...props.dropdownProps}"
    @hide="onClose"
  >
    <template #default="slotProps">
      <slot
        name="default"
        v-bind="slotProps"
      />
    </template>

    <template #dropdown="slotProps">
      <slot
        name="dropdown"
        v-bind="slotProps"
      />
    </template>
  </DropdownComponent>

  <BottomSheetComponent
    v-else
    v-bind="{ ...props.bottomSheetProps }"
    @hide="onClose"
    @save="onSave"
  >
    <template #default="slotProps">
      <slot
        name="default"
        v-bind="slotProps"
      />
    </template>

    <template v-if="$slots.title" #title>
      <slot name="title"/>
    </template>

    <template v-if="$slots.headerActions" #headerActions>
      <slot name="headerActions"/>
    </template>

    <template v-if="$slots.mobile" #sheet="slotProps">
      <slot
        name="mobile"
        v-bind="slotProps"
      />
    </template>

    <template v-else #sheet="slotProps">
      <slot
        name="dropdown"
        v-bind="slotProps"
      />
    </template>
  </BottomSheetComponent>
</template>
